/**
 * Minimal ProseMirror Editor Component
 *
 * A lightweight editor component optimized for read-only document rendering.
 * This is the foundation for replay and document viewing in Markeroo.
 *
 * Features:
 * - Read-only mode by default for viewing documents
 * - Optional editable mode
 * - Accepts optional plugins for highlights, etc.
 * - Supports initial content as ProseMirror Node
 * - whitespace-pre-wrap for proper newline rendering
 *
 * Usage:
 * ```tsx
 * // Read-only viewer (default)
 * <Editor initialContent={doc} />
 *
 * // With plugins
 * <Editor initialContent={doc} plugins={[highlightPlugin]} />
 * ```
 */

import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import type { Transaction } from "@tiptap/pm/state";
import { EditorState, Plugin } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";
import { Fragment, type Node as ProseMirrorNode } from "@tiptap/pm/model";

import { schema, createEmptyDoc } from "./schema";
import { blockKeyPlugin } from "./plugins/blockKeyPlugin";

export interface EditorProps {
  /**
   * Initial document content. If null/undefined, creates empty document.
   *
   * NOTE: This is an UNCONTROLLED prop - it's only read during initialization.
   * To programmatically update content after mount, use the `setContent()` ref method.
   *
   * @example
   * const editorRef = useRef<EditorRef>(null);
   * // Later, to update content:
   * editorRef.current?.setContent(newDoc);
   */
  initialContent?: ProseMirrorNode | null;

  /** Whether the editor is editable. Defaults to false (read-only). */
  editable?: boolean;

  /**
   * Additional ProseMirror plugins to include.
   *
   * NOTE: This is an UNCONTROLLED prop - only read during initialization.
   * Plugin changes after mount will NOT take effect. Use `useMemo` to create
   * a stable reference and avoid unnecessary re-renders.
   *
   * @example
   * const highlightPlugin = useMemo(() => createHighlightPlugin(), []);
   * <Editor plugins={[highlightPlugin]} />
   */
  plugins?: Plugin[];

  /** Placeholder text shown when document is empty. */
  placeholder?: string;

  /** Additional CSS class for the editor container. */
  className?: string;

  /** Test ID for the editor container. */
  testId?: string;

  /** ARIA label for accessibility. */
  ariaLabel?: string;
}

export interface EditorRef {
  /** Get the current document content as ProseMirror Node. */
  getContent: () => ProseMirrorNode;

  /** Get plain text content from the document. */
  getText: (options?: { blockSeparator?: string }) => string;

  /** Set new document content. */
  setContent: (content: ProseMirrorNode) => void;

  /** Focus the editor. */
  focus: () => void;

  /** Blur the editor. */
  blur: () => void;

  /** Get the underlying EditorView instance. */
  getView: () => EditorView | null;

  /** Check if editor is empty. */
  isEmpty: () => boolean;
}

/**
 * Placeholder plugin that shows placeholder text when the document is empty.
 */
function createPlaceholderPlugin(placeholder: string): Plugin {
  return new Plugin({
    props: {
      // Sets data-placeholder attribute when document is empty.
      // CSS uses this to show placeholder text via ::before pseudo-element.
      attributes(state): { [name: string]: string } {
        const doc = state.doc;
        if (
          doc.childCount === 1 &&
          doc.firstChild?.isTextblock &&
          doc.firstChild.content.size === 0
        ) {
          return { "data-placeholder": placeholder };
        }
        return {};
      },
    },
  });
}

export const Editor = forwardRef<EditorRef, EditorProps>(function Editor(
  {
    initialContent,
    editable = false,
    plugins = [],
    placeholder,
    className = "",
    testId = "editor",
    ariaLabel,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);

  // Expose editor API via ref
  useImperativeHandle(
    ref,
    () => ({
      getContent: () => {
        return viewRef.current?.state.doc ?? createEmptyDoc();
      },
      getText: (options?: { blockSeparator?: string }) => {
        const view = viewRef.current;
        if (!view) return "";
        const separator = options?.blockSeparator ?? "\n\n";
        const doc = view.state.doc;
        const parts: string[] = [];
        doc.forEach((node) => {
          if (node.isTextblock) {
            parts.push(node.textContent);
          }
        });
        return parts.join(separator);
      },
      setContent: (content: ProseMirrorNode) => {
        const view = viewRef.current;
        if (!view) return;

        // Validate schema compatibility
        if (content.type.schema !== schema) {
          console.error(
            "Editor.setContent: content must use the editor schema. " +
              "Use textToProseMirror() from @markeroo/editor/utils to convert."
          );
          return;
        }

        // Handle both doc nodes and other node types
        const fragment =
          content.type.name === "doc" ? content.content : Fragment.from(content);
        const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, fragment);
        view.dispatch(tr);
      },
      focus: () => {
        viewRef.current?.focus();
      },
      blur: () => {
        viewRef.current?.dom.blur();
      },
      getView: () => viewRef.current,
      isEmpty: () => {
        const view = viewRef.current;
        if (!view) return true;
        const doc = view.state.doc;
        return (
          doc.childCount === 1 &&
          doc.firstChild?.isTextblock === true &&
          doc.firstChild.content.size === 0
        );
      },
    }),
    []
  );

  // Dispatch transaction handler
  const dispatchTransaction = useCallback(
    (tr: Transaction) => {
      const view = viewRef.current;
      if (!view) return;

      const newState = view.state.apply(tr);
      view.updateState(newState);
    },
    []
  );

  // Initialize editor on mount
  useEffect(() => {
    if (!containerRef.current) return;

    // Build plugin list
    const allPlugins: Plugin[] = [
      blockKeyPlugin,
      ...plugins,
    ];

    // Add placeholder plugin if provided
    if (placeholder) {
      allPlugins.push(createPlaceholderPlugin(placeholder));
    }

    // Determine initial document
    const doc = initialContent ?? createEmptyDoc();

    // Validate document is using correct schema
    // If it's from a different schema (e.g., TipTap), we'll need the adapter
    let docToUse = doc;
    if (doc.type.schema !== schema) {
      console.error(
        "Editor initialization: initialContent must use the editor schema. " +
        "Use textToProseMirror() from @markeroo/editor/utils to convert. Falling back to empty document."
      );
      docToUse = createEmptyDoc();
    }

    // Create editor state
    const state = EditorState.create({
      doc: docToUse,
      plugins: allPlugins,
    });

    // Create editor view
    const view = new EditorView(containerRef.current, {
      state,
      editable: () => editable,
      dispatchTransaction,
    });

    viewRef.current = view;

    // Cleanup on unmount
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [editable, placeholder, dispatchTransaction]); // plugins/initialContent are uncontrolled (init-only)

  // Update editable state without recreating the view
  useEffect(() => {
    const view = viewRef.current;
    if (view) {
      // Force re-evaluation of editable
      view.setProps({ editable: () => editable });
    }
  }, [editable]);

  // Base styles for the editor
  // IMPORTANT: whitespace-pre-wrap ensures newlines in single-paragraph documents display correctly
  const baseClasses = [
    "tiptap",
    "prose",
    "prose-slate",
    "max-w-none",
    "focus:outline-none",
    "whitespace-pre-wrap",
    editable ? "" : "select-text",
    // Placeholder styles (works with data-placeholder attribute)
    "before:text-theme-muted",
    "before:pointer-events-none",
    "before:float-left",
    "before:h-0",
    "[&[data-placeholder]:before]:content-[attr(data-placeholder)]",
  ].filter(Boolean).join(" ");

  return (
    <div
      ref={containerRef}
      className={`${baseClasses} ${className}`}
      data-testid={testId}
      data-editable={editable}
      aria-label={ariaLabel}
    />
  );
});

export default Editor;
